if (!customElements.get('product-card-media-slideshow')) {
  class ProductCardMediaSlideshow extends HTMLElement {
    constructor() {
      super();
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onDragStart = this.onDragStart.bind(this);
      this.handleMouseLeave = this.handleMouseLeave.bind(this);
    }

    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('.card-media-slideshow__slide'));
      this.productUrl = this.dataset.productUrl;
      this.viewport = this.querySelector('.card-media-slideshow__viewport');
      this.cardWrapper = this.closest('.card-wrapper');

      this.addEventListener('click', this.onClick);
      this.cardWrapper?.addEventListener('mouseleave', this.handleMouseLeave);

      if (this.slides.length < 2) return;

      this.currentIndex = this.slides.findIndex((slide) => slide.classList.contains('is-active'));
      if (this.currentIndex < 0) this.currentIndex = 0;

      this.slides.forEach((slide) => {
        slide.draggable = false;
      });

      this.bindNavigationButton(this.querySelector('.slider-button--prev'), -1);
      this.bindNavigationButton(this.querySelector('.slider-button--next'), 1);
      this.bindSwipe();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.cardWrapper?.removeEventListener('mouseleave', this.handleMouseLeave);
      this.unbindSwipe();
    }

    bindSwipe() {
      if (!this.viewport) return;

      this.viewport.addEventListener('pointerdown', this.onPointerDown);
      this.viewport.addEventListener('dragstart', this.onDragStart);
    }

    unbindSwipe() {
      if (!this.viewport) return;

      this.viewport.removeEventListener('pointerdown', this.onPointerDown);
      this.viewport.removeEventListener('dragstart', this.onDragStart);
      this.resetPointerTracking();
    }

    onDragStart(event) {
      event.preventDefault();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.target.closest('.slider-button')) return;

      this.pointerId = event.pointerId;
      this.startX = event.clientX;
      this.startY = event.clientY;
      this.didSwipe = false;
      this.isPointerDown = true;
      this.hasCaptured = false;

      this.viewport.addEventListener('pointermove', this.onPointerMove);
      this.viewport.addEventListener('pointerup', this.onPointerUp);
      this.viewport.addEventListener('pointercancel', this.onPointerUp);
    }

    onPointerMove(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      const deltaX = event.clientX - this.startX;
      const deltaY = event.clientY - this.startY;

      if (!this.hasCaptured) {
        if (Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;

        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          this.resetPointerTracking();
          return;
        }

        this.hasCaptured = true;
        this.viewport.setPointerCapture(event.pointerId);
        this.viewport.classList.add('is-dragging');
      }

      event.preventDefault();
    }

    onPointerUp(event) {
      if (!this.isPointerDown || event.pointerId !== this.pointerId) return;

      if (this.hasCaptured) {
        const deltaX = event.clientX - this.startX;
        const threshold = Math.max(40, Math.round(this.viewport.clientWidth * 0.12));

        if (Math.abs(deltaX) >= threshold) {
          const isRtl = document.documentElement.getAttribute('dir') === 'rtl';
          const goingNext = isRtl ? deltaX > 0 : deltaX < 0;
          this.changeSlide(goingNext ? 1 : -1);
          this.didSwipe = true;
        }
      }

      this.resetPointerTracking();
    }

    resetPointerTracking() {
      if (this.hasCaptured && this.viewport.hasPointerCapture?.(this.pointerId)) {
        this.viewport.releasePointerCapture(this.pointerId);
      }

      this.isPointerDown = false;
      this.hasCaptured = false;
      this.pointerId = null;
      this.viewport?.classList.remove('is-dragging');
      this.viewport?.removeEventListener('pointermove', this.onPointerMove);
      this.viewport?.removeEventListener('pointerup', this.onPointerUp);
      this.viewport?.removeEventListener('pointercancel', this.onPointerUp);
    }

    handleMouseLeave() {
      const activeElement = document.activeElement;
      if (activeElement && this.contains(activeElement)) {
        activeElement.blur();
      }
    }

    onClick(event) {
      if (event.target.closest('.slider-button')) return;

      if (this.didSwipe) {
        event.preventDefault();
        event.stopPropagation();
        this.didSwipe = false;
        return;
      }

      if (!this.productUrl) return;
      window.location.assign(this.productUrl);
    }

    bindNavigationButton(button, direction) {
      if (!button) return;

      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.changeSlide(direction);
        button.blur();
      });
    }

    changeSlide(direction) {
      this.slides[this.currentIndex]?.classList.remove('is-active');
      this.currentIndex = (this.currentIndex + direction + this.slides.length) % this.slides.length;
      this.slides[this.currentIndex]?.classList.add('is-active');
    }
  }

  customElements.define('product-card-media-slideshow', ProductCardMediaSlideshow);
}
